import type { ToolSet } from 'ai';
import type { Observable } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { dynamicTool, jsonSchema } from 'ai';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, firstValueFrom, race, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import type { Client } from '@modelcontextprotocol/sdk/client/index.js';
import type { CallToolResult } from '@modelcontextprotocol/sdk/types.js';

import type { Uuid } from '@common/types';
import { isUuid } from '@common/helpers';

import { SiteFacade } from '@web/store/website/site/site.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import SiteActions from '@web/store/website/site/site.actions';
import ContentActions from '@web/store/website/content/content.actions';
import { ContentFacade } from '@web/store/website/content/content.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { HistoryFacade } from '@web/store/history/history.facade';
import type { BatchEntry } from '@web/store/history/history.model';
import { HistoryChangeAction } from '@web/store/history/history.model';

/**
 * Same-origin RELATIVE path, deliberately — the exact shape `site.http.service.ts` uses for
 * `/websites/builder/api/preview`, so the MCP server rides the routing that already exists rather
 * than needing its own host: Apache maps `/websites/builder/` onto this stack's `/websites/`, and
 * the `mcp` router in `docker/traefik/dynamic.yml` strips `/websites/api` before it reaches the
 * server. Same-origin also means no CORS preflight in deployed environments.
 *
 * Locally, `apps/websites/self-proxy.conf.js` forwards this path to a `nx run mcp-server:serve-http`
 * on 3939 — which is why the port no longer appears here.
 */
const MCP_SERVER_URL = '/websites/builder/api/mcp';

/**
 * Same cookie `JwtInterceptor` reads for every other API call. Read directly here because the MCP
 * transport talks over raw `fetch`, not Angular's `HttpClient` — no interceptor runs on it, so this
 * service has to attach the header itself.
 */
const ACCESS_TOKEN_COOKIE = 'oauth_access_token';

/**
 * How long a fetched tool list is reused before `getTools()` fetches a fresh one — long enough to
 * skip the `listTools()` round trip (an await on the critical path of every send, per
 * `ai-mcp-plan.md`'s "Слабые места текущего флоу" table) for the common case of several messages sent
 * in a row, short enough that editing the prototype server's tools during dev still shows up within
 * one reply, without needing to reload the chat drawer.
 */
const TOOLS_CACHE_TTL_MS = 30_000;

/**
 * How long the end-of-reply re-read may hold the canvas blocked. Generous — a big page plus
 * `updateWidgetsData`'s product lookups is seconds, not milliseconds — but finite, because the
 * failure it guards is an editor the user cannot unblock without reloading.
 */
const RESYNC_TIMEOUT_MS = 30_000;

/**
 * How long the pre-flight flush may hold the first tool call. Creating a site is four requests
 * back to back (`POST sites`, then pages/domains/settings) plus a router navigation, so it is not
 * quick — but a flush that never resolves would hang the reply with the canvas blocked, which is
 * the one outcome worse than refusing.
 */
const FLUSH_TIMEOUT_MS = 20_000;

/** The three entity kinds AI history tracking covers — see `AiHistoryTouch`. */
type AiHistoryEntityType = 'block' | 'column' | 'widget';

/**
 * One id's net effect on the page, for one AI reply, on its way to becoming a `BatchEntry` —
 * see `collectAiHistoryTouches`, `refreshAiHistoryAfterSnapshots` and `flushAiHistoryBatch`.
 *
 * `entity` is the DENORMALIZED form (children inlined) that `BlocksFacade.addBlock`/`removeBlock`
 * and `ColumnsFacade.addColumn`/`removeColumn` take — a different shape than `update`'s `before`/
 * `after`, which carry only the flat fields a manual edit's own history entry already uses.
 */
type AiHistoryTouch =
    | {
          kind: 'update';
          entityType: AiHistoryEntityType;
          id: string;
          before: Record<string, unknown>;
          after: Record<string, unknown>;
      }
    | { kind: 'add'; entityType: AiHistoryEntityType; id: string; index: number; entity?: unknown }
    | { kind: 'remove'; entityType: AiHistoryEntityType; id: string; index: number; entity: unknown };

/**
 * Whether a tool result reports a change to the THEME.
 *
 * Checked on the result rather than on the tool name for the same reason `noteServerChange` reads
 * the tag rather than switching on the name: a theme tool added later is covered without an edit
 * here. A BATCH carries its entries under `results`, each tagged the same way, so one theme edit
 * among four structural ones still counts.
 *
 * `entity` is read alongside `entityType` because that is what the tag was called before the
 * mutation envelope renamed it — an older server must not silently stop refreshing the palette.
 */
function mentionsTheme(mapped: Record<string, unknown>): boolean {
    if (mapped['entityType'] === 'theme' || mapped['entity'] === 'theme') {
        return true;
    }

    const results = mapped['results'];

    return Array.isArray(results) && results.some((result) => mentionsTheme(result as Record<string, unknown>));
}

/**
 * Whether a tool result reports a change to one of the site's SHARED blocks — the header or footer.
 *
 * Needs the same bigger re-read as a theme change, and for the identical reason: shared blocks are
 * read off the SITE content (`getSiteContentSharedBlocks`), which the ordinary resync takes from the
 * store instead of re-fetching. Without this the AI's header edit is written on the server, never
 * reaches the canvas, and is then overwritten by the editor's next autosave — which still holds the
 * header from page load.
 *
 * `shared` is set by `sharedNote()` in mcp-backend's `operations.ts` on every block outcome that
 * touched a cross-page block, and carried through by the mutation envelope. Read off the RESULT
 * rather than the tool name because the tool is an ordinary one: `save_column` on a column inside
 * the header is a shared-block change, and nothing in its name says so.
 */
function mentionsSharedBlock(mapped: Record<string, unknown>): boolean {
    if (mapped['shared'] === 'header' || mapped['shared'] === 'footer') {
        return true;
    }

    const results = mapped['results'];

    return Array.isArray(results) && results.some((result) => mentionsSharedBlock(result as Record<string, unknown>));
}

/**
 * The chat drawer's only source of tools. Connects `apps/mcp-server` to the drawer's
 * tool-calling loop over Streamable HTTP, discovers its tools via
 * `listTools()`, and wraps each one as an AI SDK `dynamicTool()` — the installed `ai` version
 * (7.0.15) ships no first-class MCP client integration, so `@ai-sdk/provider-utils`'s
 * `dynamicTool()` (the documented escape hatch for a tool whose schema is only known at runtime)
 * plus `jsonSchema()` (to wrap the raw JSON Schema `tools/list` returns, instead of a Zod schema)
 * stands in for it.
 *
 * `apps/mcp-server`'s tools now call `apps/mcp-backend`, which mutates the REAL persisted
 * page (via the real Laravel `landings-service`) — but that write happens entirely server-side;
 * the browser's own NgRx entity stores have no way to find out about it on their own. So this
 * service re-reads the page from the server whenever a tool reports having changed it, and the
 * chat transport drives that re-read after every step of the reply — which is what makes the
 * change show up on the open page instead of only after a manual reload. See `noteServerChange()`
 * and `resyncOpenPageIfChanged()`.
 *
 * It used to MIRROR each mutation into the stores instead (§4.3 variant B) — 445 lines and a
 * twelve-branch switch, all of it a second implementation of a change the server had already made.
 * That is gone; see `noteServerChange()` for why the progressive updates survived without it.
 *
 * Which page a call mutates is decided HERE, not by the server's configuration and not by the
 * model: `fetchWithPageContext` stamps the open site/page and the user's own token onto every HTTP
 * request, and the server scopes that request to them (see `http-app.ts`). The server therefore
 * holds no page state of its own and serves any number of tabs at once.
 *
 * `noteServerChange` still checks the `siteId`/`pageId` every result carries (`McpBackendClient`'s
 * `PageContext`) against the open page. That should now always match; it stays as a cheap guard,
 * because the failure it prevents — resetting the user's page for a change made somewhere else —
 * is pure noise to them.
 *
 * `@modelcontextprotocol/sdk`'s client (plus its `ajv`/`ajv-formats` validation deps) is loaded
 * via a dynamic `import()`, not a top-level one: pulling it in statically added ~360KB to the
 * app's INITIAL bundle and tripped the production size budget — real weight paid by every user
 * on every page load for a toggle that only exists for local dev testing. The dynamic import
 * keeps it in its own lazy chunk, fetched only the first time someone actually flips the toggle.
 */
/**
 * The one error worth reconnecting for — `apps/mcp-server`'s `http-app.ts` answers exactly these two
 * when it does not hold the session the request names.
 */
function isLostSession(error: unknown): boolean {
    const code = (error as { code?: unknown } | null)?.code;
    const message = error instanceof Error ? error.message : String(error ?? '');

    return code === -32001 || /session not found|No valid session ID/i.test(message);
}

@Injectable({ providedIn: 'root' })
export class McpToolsService {
    private readonly store = inject(Store);
    private readonly siteFacade = inject(SiteFacade);
    private readonly pagesFacade = inject(PagesFacade);
    private readonly cookieService = inject(CookieService);
    private readonly contentFacade = inject(ContentFacade);
    private readonly actions$ = inject(Actions);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly historyFacade = inject(HistoryFacade);

    private connection: Promise<Client> | null = null;

    /**
     * Entities this reply has created, updated or removed so far, keyed by `${entityType}:${id}` —
     * one undo-stack entry per id even if the model touched it several times. See
     * `collectAiHistoryTouches`, `refreshAiHistoryAfterSnapshots` and `flushAiHistoryBatch` for how
     * each kind is filled in and turned into a `BatchEntry`.
     *
     * `moved` (reorder) is not tracked — still not covered, see ticket #69719 §6.
     */
    private readonly aiHistoryTouched = new Map<string, AiHistoryTouch>();

    /**
     * Set by `noteServerChange` the moment a reply's first mutation comes back, cleared by
     * `resyncOpenPageIfChanged()`. Exists so a purely conversational reply doesn't reset the
     * editor for nothing — see that method.
     */
    private pageChangedDuringReply = false;

    /**
     * Whether one of this reply's mutations touched something stored on the SITE document rather
     * than on the page: the theme, or one of the shared blocks (header/footer).
     *
     * Tracked separately from `pageChangedDuringReply` because it needs a bigger re-read. The
     * ordinary resync reuses the site content from the store instead of re-fetching it, so without
     * this flag the change is written on the server, never appears on the canvas, and is then
     * overwritten by the editor's next autosave — which still holds what page load gave it. That is
     * not a hypothetical: it is what a palette write did before the flag existed, and a header edit
     * would fail the same way for the same reason.
     */
    private siteDocumentChangedDuringReply = false;

    /** The single in-flight pre-flight flush — see `ensureServerHasLatest`. */
    private flushing: Promise<Record<string, unknown> | null> | null = null;

    /**
     * The in-flight/last `getTools()` result, reused within `TOOLS_CACHE_TTL_MS` — see that constant.
     * Cleared on a lost-session reconnect (`withSession`'s catch) since a new session is the one event
     * that already signals "don't assume anything about server state".
     */
    private toolsCache: { tools: Promise<ToolSet>; fetchedAt: number } | null = null;

    public async getTools(): Promise<ToolSet> {
        if (this.toolsCache && Date.now() - this.toolsCache.fetchedAt < TOOLS_CACHE_TTL_MS) {
            console.log(
                'AI chat (MCP): reusing cached tool list (fetched',
                Date.now() - this.toolsCache.fetchedAt,
                'ms ago)'
            );
            return this.toolsCache.tools;
        }

        const fetchedAt = Date.now();
        const tools = this.fetchTools();

        this.toolsCache = { tools, fetchedAt };

        try {
            return await tools;
        } catch (error) {
            // Don't cache a failed fetch — the next getTools() call should try again from scratch.
            this.toolsCache = null;
            throw error;
        }
    }

    private async fetchTools(): Promise<ToolSet> {
        const { tools } = await this.withSession((client) => client.listTools());

        // What the model is about to be offered, by name. One line per message, next to the existing
        // per-call logs — the cheap answer to "did the model actually have that tool?", which
        // otherwise takes a wire probe against the server and still cannot say what the BROWSER got.
        console.log(`AI chat (MCP): ${tools.length} tools`, tools.map((tool) => tool.name).join(', '));
        // Full definitions (description + inputSchema, i.e. every parameter) for each one — the
        // `activeTools` filter downstream only trims what reaches the provider, so this is the
        // complete surface the browser fetched, not what any one step actually sends.
        console.log('AI chat (MCP): full tool definitions', tools);

        return tools.reduce<ToolSet>((acc, mcpTool) => {
            acc[mcpTool.name] = dynamicTool({
                description: mcpTool.description ?? '',
                inputSchema: jsonSchema(mcpTool.inputSchema as Parameters<typeof jsonSchema>[0]),
                execute: async (input) => {
                    const refusal = await this.ensureServerHasLatest();

                    if (refusal) {
                        console.warn(`AI chat (MCP): refusing "${mcpTool.name}" — editor state not on the server`);
                        return refusal;
                    }

                    console.log(`AI chat (MCP): tool call "${mcpTool.name}"`, input);
                    const result = await this.withSession((client) =>
                        client.callTool({
                            name: mcpTool.name,
                            arguments: input as Record<string, unknown>,
                        })
                    );
                    console.log(`AI chat (MCP): tool call "${mcpTool.name}" result`, result);

                    const mapped = this.toExecuteResult(result as CallToolResult);
                    await this.noteServerChange(mcpTool.name, mapped);

                    return mapped;
                },
            });
            return acc;
        }, {});
    }

    /**
     * Makes the server's copy of the page match the browser's BEFORE the first tool call, or
     * returns a refusal to hand the model when it cannot.
     *
     * Two states need this, and they turn out to be the same state seen twice:
     *
     * - **A draft.** A site or page whose id is a UUID has never been saved (`SiteService.isNewSite`
     *   /`isNewPage`; `content.effects.ts` skips the content request for such ids and synthesizes an
     *   empty page locally). `apps/mcp-backend` read-modify-writes through the real
     *   landings-service, so there is nothing to read and nothing to write.
     * - **Pending edits.** The page IS saved, but the user changed something inside the autosave's
     *   3s debounce — or while it was paused, which a zero-block drag does. mcp-backend would then
     *   read a structure older than the one on screen, the model would edit that, and the pending
     *   autosave would later overwrite the result.
     *
     * They are one state because the draft window is defined by the autosave: it ends at the first
     * structural action, since that is exactly what arms the autosave. So "there is something the
     * server does not have yet" covers both, and `isDirty` — set by the same effect, off the same
     * action list — is precisely that question.
     *
     * The fix is one dispatch. `saveSiteRequest` routes itself on `isNewSite`/`isNewPage`/else into
     * create-site / create-page / update-page, so a draft, a new page and a dirty saved page all go
     * through their own correct path with no branching here. It is also the exact action the
     * autosave ends up dispatching, which is why this creates nothing the editor would not have
     * created seconds later on its own — and why it bypasses the name/domain modal, which hangs on
     * the explicit Save/Publish path in `modals.effects.ts`, not on this action.
     *
     * Deliberately NOT done at the start of a reply: a conversational turn ("what can you do?")
     * must not create a site. It sits on the tool call because that is where the user has actually
     * asked for the page to be touched.
     *
     * Returned as a normal tool result rather than thrown: the model reads it, relays it, and can
     * still answer questions in the same turn. Throwing would fail the whole message.
     */
    private async ensureServerHasLatest(): Promise<Record<string, unknown> | null> {
        // One flush per reply, however many tool calls race into it. Without this, two calls
        // arriving together on a draft would each dispatch `saveSiteRequest` and create TWO sites.
        this.flushing ??= this.flushEditorState().finally(() => {
            this.flushing = null;
        });

        return this.flushing;
    }

    private async flushEditorState(): Promise<Record<string, unknown> | null> {
        const [siteId, page, isDirty, isSaving] = await Promise.all([
            firstValueFrom(this.siteFacade.id$),
            firstValueFrom(this.pagesFacade.currentPage$),
            firstValueFrom(this.contentFacade.isDirty$),
            firstValueFrom(this.siteFacade.isSaving$),
        ]);

        const unsaved = [isUuid(siteId as Uuid) ? 'site' : null, isUuid(page?.id as Uuid) ? 'page' : null].filter(
            Boolean
        );

        if (!unsaved.length && !isDirty) {
            return null;
        }

        // A save already in flight (an autosave that fired just before the reply began) is left to
        // finish rather than joined: `saveSiteRequest` has no in-flight guard of its own — the one
        // in `saveSite$` sits on the action BEFORE it — so dispatching over a running create would
        // POST a second site.
        if (!isSaving) {
            this.siteFacade.saveSiteRequest(false, false, page?.id);
        }

        try {
            await this.awaitFlushed();
            return null;
        } catch (error) {
            const reason = error instanceof Error ? error.message : String(error);
            console.warn('AI chat (MCP): could not flush the editor before the tool call —', reason);

            return {
                ok: false,
                message:
                    (unsaved.length
                        ? `This ${unsaved.join(' and ')} has never been saved and saving it just now did not ` +
                          'succeed, so it does not exist on the server and cannot be edited through these tools. '
                        : 'The page has unsaved changes and saving them just now did not succeed, so editing it ' +
                          'through these tools would work from an outdated copy and lose those changes. ') +
                    'Tell the user to save the page manually and try again. Do not retry, and do not claim ' +
                    'anything was created or changed.',
            };
        }
    }

    /**
     * Resolves once the store shows a page the server has: real (non-UUID) ids and nothing dirty.
     *
     * Both halves are needed. The ids alone miss the already-saved dirty page; `isDirty` alone is
     * not enough because a create leaves it false the whole time — a fresh draft was never dirty to
     * begin with.
     *
     * `saveSiteFailure` has to be raced explicitly rather than inferred from the state:
     * `SiteService.handleSaveSiteError` dispatches `setIsDirty({ isDirty: false })` alongside it, so
     * a failed save looks exactly like a successful one to the condition above.
     */
    private awaitFlushed(): Promise<void> {
        const settled$ = combineLatest([
            this.siteFacade.id$,
            this.pagesFacade.currentPage$,
            this.contentFacade.isDirty$,
        ]).pipe(
            filter(
                ([siteId, page, isDirty]) =>
                    !isUuid(siteId as Uuid) && page?.id != null && !isUuid(page.id as Uuid) && !isDirty
            ),
            map(() => undefined as void)
        );

        const failed$ = this.actions$.pipe(
            ofType(SiteActions.saveSiteFailure),
            map(({ error }) => {
                throw new Error(`save failed: ${error ?? 'unknown error'}`);
            })
        );

        const timedOut$ = timer(FLUSH_TIMEOUT_MS).pipe(
            map(() => {
                throw new Error(`save did not finish within ${FLUSH_TIMEOUT_MS}ms`);
            })
        );

        return firstValueFrom(race(settled$, failed$, timedOut$).pipe(take(1)));
    }

    /**
     * MCP's `CallToolResult` (`content`/`structuredContent`/`isError`) has no equivalent in the
     * AI SDK's tool-execute contract — flatten it into a plain object so the model sees a plain
     * `{ ok, ... }` shape instead of raw MCP wire format.
     */
    private toExecuteResult(result: CallToolResult): Record<string, unknown> {
        const message = result.content
            .filter((part): part is { type: 'text'; text: string } => part.type === 'text')
            .map((part) => part.text)
            .join('\n');

        return {
            ok: !result.isError,
            message,
            ...(result.structuredContent ?? {}),
        };
    }

    /**
     * True when the site/page the MCP result mutated (tagged onto every result by
     * `McpBackendClient`'s `PageContext`) matches the page currently open in this browser tab.
     * Compared as strings because ids cross a JSON boundary and the app types them loosely
     * (`Id | Uuid` — number or string). A missing tag (older server, or a tool that doesn't carry
     * one) is treated as "matches" so the guard never silently swallows a legitimate update — the
     * guard exists to catch a real cross-page mismatch, not to hard-require the tag.
     */
    private async targetsOpenPage(mapped: Record<string, unknown>): Promise<boolean> {
        const resultSiteId = mapped['siteId'];
        const resultPageId = mapped['pageId'];

        if (resultSiteId == null || resultPageId == null) {
            return true;
        }

        const [openSiteId, openPage] = await Promise.all([
            firstValueFrom(this.siteFacade.id$),
            firstValueFrom(this.pagesFacade.currentPage$),
        ]);

        return String(resultSiteId) === String(openSiteId) && String(resultPageId) === String(openPage?.id);
    }

    /**
     * Re-reads the open page from the server and rebuilds the entity stores from it — §4.3 variant
     * A, the backstop under the per-step mirroring that variant B does.
     *
     * Why both: the mirror applies each mutation as it happens, so the canvas keeps up during the
     * tool loop, but it is a SECOND implementation of a change the server already made — one that
     * dispatches flat store actions where the server spliced a nested tree. The two can drift (a
     * reducer side effect the server computes differently, a tag this build doesn't know). Re-reading
     * once at the end collapses whatever drift accumulated, so the mirror only has to be right
     * enough to look right for a few seconds, not authoritative until the next reload.
     *
     * Deliberately `loadPageContentRequest` rather than anything new: that action already re-fetches
     * the page content, merges the site's shared blocks back in (the header and footer live in
     * `draft_shared_blocks`, NOT in the structure mcp-backend writes — rebuilding the stores without
     * that step would drop them off the canvas) and runs the same `setPageStructure` the page uses
     * on open. Site content is reused from the store rather than re-fetched, so this is one request.
     *
     * Costs, both accepted in §4.3: the stores are replaced wholesale, so selection and open panels
     * reset and the content loader flashes once per reply — at a moment the user is already waiting.
     * And it takes the SERVER's copy, so an editor edit made during the reply and not yet
     * autosaved is lost; the real fix for that direction is the save-time version check (§2.1) and
     * locking the canvas during a reply (§7), neither of which exists yet.
     */
    public async resyncOpenPageIfChanged(): Promise<void> {
        if (!this.pageChangedDuringReply) {
            return;
        }

        this.pageChangedDuringReply = false;

        const reloadSite = this.siteDocumentChangedDuringReply;

        this.siteDocumentChangedDuringReply = false;

        // Never rejects, by contract. Its one caller is the chat transport's `finally`, where the
        // controller is closed — a rejection there would skip `controller.close()` and leave the
        // reply stream open forever, i.e. a chat that spins with no way out. Failing to re-read is
        // a stale canvas until the next reload; failing to close is a dead chat. The dispatch below
        // kicks off a whole content-reload path (effects, reducers), so this is not hypothetical.
        try {
            const [siteId, page] = await Promise.all([
                firstValueFrom(this.siteFacade.id$),
                firstValueFrom(this.pagesFacade.currentPage$),
            ]);

            if (siteId == null || page?.id == null) {
                return;
            }

            this.store.dispatch(ContentActions.loadPageContentRequest({ siteId, pageId: page.id, reloadSite }));

            // Awaited, not fire-and-forget: the canvas blocker (§7) is released when this resolves,
            // and releasing it while the stores are still being rebuilt would hand the user a
            // half-replaced page to click on — the exact window the blocker exists to close.
            //
            // The reducer flips `isLoading` synchronously on the dispatch above, so there is no race
            // where this sees the PREVIOUS false and returns immediately.
            //
            // Bounded: an effect that throws before its `finally` would otherwise leave the editor
            // blocked forever, and a stale canvas is a far better failure than a frozen one.
            const reloaded = firstValueFrom(
                this.contentFacade.isLoading$.pipe(
                    filter((isLoading) => !isLoading),
                    take(1)
                )
            );

            await Promise.race([reloaded, this.giveUpAfterTimeout()]);

            // The store now holds server truth for everything this reply has touched so far —
            // the right moment to refresh the "after" half of any pending AI history entry. Not
            // gated on which ids actually changed in THIS resync (there is no cheap way to tell,
            // now that the entity itself is stripped from every mutation response — see
            // `mutation-envelope.ts`): re-reading a handful of already-settled ids too is
            // negligible next to the content reload just awaited above.
            this.refreshAiHistoryAfterSnapshots();
        } catch (error) {
            console.error(
                'AI chat (MCP): could not re-read the page after the reply — the canvas may be out of ' +
                    'date until the next reload. The page itself was written correctly on the server.',
                error
            );
        }
    }

    /**
     * Rejects once `RESYNC_TIMEOUT_MS` has passed — the losing half of the race above.
     *
     * A plain `setTimeout` rather than rxjs's `timeout()` operator: this has to be verifiable, and
     * the operator's scheduler does not advance under Angular's `fakeAsync`, which left the guard
     * that keeps the editor from freezing permanently untestable. Same behaviour, one primitive,
     * and a test that actually exercises it.
     */
    private giveUpAfterTimeout(): Promise<never> {
        return new Promise((_resolve, reject) => {
            setTimeout(
                () => reject(new Error(`page re-read did not finish within ${RESYNC_TIMEOUT_MS}ms`)),
                RESYNC_TIMEOUT_MS
            );
        });
    }

    /**
     * Decides whether this tool result means the open page has to be re-read, and refuses the two
     * cases where it must not be.
     *
     * **Why this no longer mirrors anything.** It used to apply each mutation to the NgRx stores as
     * it arrived (§4.3 variant B) — 445 lines, a twelve-branch `entity × op` switch, and one
     * `sync*` method per shape. All of it was a SECOND implementation of a change the server had
     * already made, written in flat store actions where the server splices a nested tree, and made
     * non-authoritative by the end-of-step re-read that runs anyway. The drift it produced was not
     * hypothetical: the index the backend actually clamped to, entities read from pre-v0 keys, and
     * the stub's echo all shipped as bugs found through it.
     *
     * The thing variant B was really for — the canvas keeping up during a multi-step reply — turns
     * out not to need a mirror at all, only a REFRESH. `SendpulseChatTransport` now calls
     * `resyncOpenPageIfChanged()` after every step instead of once at the end, so the page still
     * updates as the reply progresses, with one implementation instead of two. Adding a widget type
     * consequently touches nothing in this file.
     *
     * The `entity`/`op` tag is still read, for one bit of information: whether anything changed at
     * all. A purely conversational reply must not reset the editor for nothing. The tag comes from
     * `sendMutation` in `apps/mcp-backend/src/routes/shared.ts`, the only way that service can
     * answer a mutation with `200`/`201`.
     */
    private async noteServerChange(toolName: string, mapped: Record<string, unknown>): Promise<void> {
        if (mapped['ok'] !== true) {
            return;
        }

        // The MCP server is running against its in-memory stub (`MCP_BACKEND_STUB=1`), so this
        // "success" wrote nothing to the page. Re-reading would pull the UNCHANGED page and quietly
        // undo whatever the user believes just happened, which reads as "the AI did nothing" rather
        // than as "the server is a stub".
        //
        // Refused on the marker the stub sets on every response rather than on the shape of the
        // entity: text, social and button legitimately have no `styles` group, so a structural test
        // would reject real widgets. See `fake-mcp-backend.ts`.
        if (mapped['_stub'] === true) {
            console.error(
                `AI chat (MCP): "${toolName}" was answered by the MCP server's in-memory STUB — nothing was ` +
                    'written to this page. Not re-reading. Start the server without MCP_BACKEND_STUB ' +
                    '(and have apps/mcp-backend running).',
                { toolName, result: mapped }
            );
            return;
        }

        if (!(await this.targetsOpenPage(mapped))) {
            // The MCP server mutated a DIFFERENT page than the one open here. The write landed there
            // server-side; re-reading THIS page would be pointless work, and treating it as changed
            // would reset the user's selection for a change they will never see here.
            console.warn(
                'AI chat (MCP): tool result targeted a different site/page than the one open — ' +
                    'not re-reading the open page',
                { resultSiteId: mapped['siteId'], resultPageId: mapped['pageId'] }
            );
            return;
        }

        // A read-only tool (`get_page_snapshot`) carries neither half of the tag — it changed
        // nothing. Checked as "neither" rather than by tool name so a future read-only tool needs no
        // edit here either. Anything tagged at all counts as "the page changed", including a tag
        // this build does not recognize: reaching here means the server already wrote, and the
        // re-read is exactly what covers a mutation this build knows nothing about.
        //
        // `entityType` is what the mutation envelope calls it; `entity` was its name before that and
        // is still read so an older server does not silently stop updating the canvas. `op` is
        // present in both, and `apply_operations` reports `op: 'batch'` — a batch is several changes,
        // which is all this needs to know.
        if (mapped['entityType'] == null && mapped['entity'] == null && mapped['op'] == null) {
            return;
        }

        this.pageChangedDuringReply = true;
        this.collectAiHistoryTouches(mapped);

        if (mentionsTheme(mapped) || mentionsSharedBlock(mapped)) {
            this.siteDocumentChangedDuringReply = true;
        }
    }

    /**
     * Finds every `entityType: 'block' | 'column' | 'widget'` envelope inside this tool result —
     * the top-level envelope itself, or, for `apply_operations`, each entry under `results` — and
     * updates `aiHistoryTouched` for it. `moved` is not one of the ops handled here — see the field
     * doc on `aiHistoryTouched`.
     */
    private collectAiHistoryTouches(envelope: Record<string, unknown>): void {
        if (envelope['op'] === 'batch' && Array.isArray(envelope['results'])) {
            for (const result of envelope['results'] as Record<string, unknown>[]) {
                this.collectAiHistoryTouches(result);
            }
            return;
        }

        const { entityType, id, op } = envelope;

        if (typeof id !== 'string' || (entityType !== 'block' && entityType !== 'column' && entityType !== 'widget')) {
            return;
        }

        if (op === 'created') {
            this.noteAiHistoryCreate(entityType, id, envelope['index']);
        } else if (op === 'updated') {
            this.noteAiHistoryUpdate(entityType, id);
        } else if (op === 'removed') {
            this.noteAiHistoryRemove(entityType, id, envelope['index']);
        }
    }

    /**
     * A create the AI just made. The entity itself isn't in the store yet (this runs before this
     * reply's resync), so `entity` is filled in later, by `refreshAiHistoryAfterSnapshots` — undo
     * just needs to know an id, an entityType and an index exist at all.
     */
    private noteAiHistoryCreate(entityType: AiHistoryEntityType, id: string, index: unknown): void {
        if (typeof index !== 'number') {
            return;
        }

        this.aiHistoryTouched.set(`${entityType}:${id}`, { kind: 'add', entityType, id, index });
    }

    /** An update the AI just made — seeds `before` the first time this id comes up this reply. */
    private noteAiHistoryUpdate(entityType: AiHistoryEntityType, id: string): void {
        const key = `${entityType}:${id}`;
        const existing = this.aiHistoryTouched.get(key);

        if (existing) {
            return;
        }

        // Read now, before this reply's next tool call can move the store further — this is the
        // value from right before whatever just changed it reached the store (its own resync, if
        // this is the first time this id comes up, hasn't run yet).
        const before = this.readEntitySnapshot(entityType, id);

        if (before) {
            this.aiHistoryTouched.set(key, { kind: 'update', entityType, id, before, after: before });
        }
    }

    /**
     * A remove the AI just made. `entity` has to be captured NOW, denormalized, from the store —
     * still pre-resync, so still there — because the removed envelope carries only the id (see
     * `mutation-envelope.ts`'s doc comment) and there will be nothing left to read once resynced.
     *
     * An id already tracked as `add` (created and removed again within this SAME reply) nets to
     * nothing — it never existed before this reply, so there is nothing for undo to restore, and
     * the entry is dropped rather than turned into a `remove`. An id tracked as `update` is
     * overwritten: what undo needs to restore is the entity as it stood right before THIS removal,
     * which is exactly what gets read here, superseding the update's own (differently-shaped,
     * flat-fields-only) `before`.
     */
    private noteAiHistoryRemove(entityType: AiHistoryEntityType, id: string, index: unknown): void {
        const key = `${entityType}:${id}`;

        if (this.aiHistoryTouched.get(key)?.kind === 'add') {
            this.aiHistoryTouched.delete(key);
            return;
        }

        if (typeof index !== 'number') {
            return;
        }

        const entity = this.readDenormalizedSnapshot(entityType, id);

        if (entity) {
            this.aiHistoryTouched.set(key, { kind: 'remove', entityType, id, index, entity });
        }
    }

    /**
     * The undo-relevant fields of one entity, read synchronously from its facade — the same fields
     * `web-block-form.service.ts`/`web-column-form.service.ts`/`web-widget-signal-form.service.ts`
     * already use as `changes` for a manual edit, so an AI-produced `BatchUpdateEntry` undoes
     * through the exact same `HistoryServiceV2.applyUpdate` path. `undefined` when the id isn't in
     * the store (yet) — e.g. an id from a create this same reply made, before its own resync ran.
     */
    private readEntitySnapshot(entityType: AiHistoryEntityType, id: string): Record<string, unknown> | undefined {
        switch (entityType) {
            case 'block': {
                const block = this.readSnapshot(this.blocksFacade.blockById$(id as Uuid));

                return block && { props: block.props, meta: block.meta };
            }
            case 'column': {
                const column = this.readSnapshot(this.columnsFacade.columnById$(id as Uuid));

                return column && { props: column.props };
            }
            case 'widget': {
                const widget = this.readSnapshot(this.widgetsFacade.widgetById$(id as Uuid));

                return widget && { data: widget.data };
            }
        }
    }

    /**
     * The full, DENORMALIZED entity (children inlined) — what `BlocksFacade.addBlock`/`removeBlock`
     * and `ColumnsFacade.addColumn`/`removeColumn` actually take, unlike `readEntitySnapshot`'s flat
     * fields. A widget has no nested children of its own, so its plain, non-denormalized value
     * already IS the right shape and needs no separate read.
     */
    private readDenormalizedSnapshot(entityType: AiHistoryEntityType, id: string): unknown {
        switch (entityType) {
            case 'block':
                return this.readSnapshot(this.blocksFacade.blockStructureEntityById$(id as Uuid));
            case 'column':
                return this.readSnapshot(this.columnsFacade.denormalizedColumnById$(id as Uuid));
            case 'widget':
                return this.readSnapshot(this.widgetsFacade.widgetById$(id as Uuid));
        }
    }

    /** `Store.select()` emits synchronously on subscribe, so this reads the CURRENT value, not a stream. */
    private readSnapshot<T>(source: Observable<T>): T | undefined {
        let value: T | undefined;

        source.subscribe((emitted) => (value = emitted)).unsubscribe();

        return value;
    }

    /**
     * Refreshed from the (just-resynced) store — see `resyncOpenPageIfChanged`'s call site: an
     * `update` touch's `after` half, and an `add` touch's `entity`. Overwrites unconditionally
     * rather than only for ids this particular resync round actually changed, because the mutation
     * envelope no longer says which ids that was (see `mutation-envelope.ts`'s doc comment) —
     * re-reading a few already-settled ids is cheap next to the content reload that just ran.
     *
     * Unconditional matters for `add` specifically: a widget created in step 1 and then edited in
     * step 2 must end up with step 2's content, not step 1's — so this keeps re-reading it on every
     * resync rather than stopping the first time it finds something. An `add` touch whose entity is
     * still nowhere to be found (a create the model reported but that never made it to the store)
     * stays `undefined` and is dropped later, at flush.
     */
    private refreshAiHistoryAfterSnapshots(): void {
        for (const touched of this.aiHistoryTouched.values()) {
            if (touched.kind === 'update') {
                const after = this.readEntitySnapshot(touched.entityType, touched.id);

                if (after) {
                    touched.after = after;
                }
            } else if (touched.kind === 'add') {
                touched.entity = this.readDenormalizedSnapshot(touched.entityType, touched.id) ?? touched.entity;
            }
        }
    }

    /**
     * Turns this reply's `aiHistoryTouched` map into one undo step and clears the map — call exactly
     * once per reply, after the final `resyncOpenPageIfChanged()` (see `sendpulse-chat-transport.ts`'s
     * `finally`), never per step: `resyncOpenPageIfChanged` itself runs once per step, which is why
     * the map exists instead of pushing straight to history from there.
     *
     * An `update` whose `before` and `after` end up identical (a call that got made but changed
     * nothing once fully resynced) is dropped rather than turned into a no-op undo step. An `add`
     * whose `entity` never got filled in (see `refreshAiHistoryAfterSnapshots`) is dropped the same
     * way — nothing to redo it WITH.
     */
    public flushAiHistoryBatch(): void {
        const entries: BatchEntry[] = [];

        for (const touched of this.aiHistoryTouched.values()) {
            // The AI edits legacy widgets only; in the batch history `widget` is the versioned envelope.
            const entityType = touched.entityType === 'widget' ? 'legacyWidget' : touched.entityType;

            if (touched.kind === 'update') {
                if (JSON.stringify(touched.before) !== JSON.stringify(touched.after)) {
                    entries.push({
                        action: HistoryChangeAction.update,
                        entityType,
                        before: { id: touched.id, changes: touched.before },
                        after: { id: touched.id, changes: touched.after },
                    });
                }
            } else if (touched.kind === 'add') {
                if (touched.entity !== undefined) {
                    entries.push({
                        action: HistoryChangeAction.add,
                        entityType,
                        entity: touched.entity,
                        index: touched.index,
                    });
                }
            } else {
                entries.push({
                    action: HistoryChangeAction.remove,
                    entityType,
                    entity: touched.entity,
                    index: touched.index,
                });
            }
        }

        this.aiHistoryTouched.clear();
        this.historyFacade.addAiBatchToHistory(entries);
    }

    /**
     * Runs one call on the shared client, re-establishing the session once if it has gone.
     *
     * **Why this is needed.** `apps/mcp-server` keeps its transports in an in-memory map keyed by
     * `Mcp-Session-Id` and answers `session not found` (-32001) for an id it does not hold. The map
     * dies with the process, so every restart of that server — which in development is constant —
     * leaves an open editor tab holding a dead id. Nothing recovered: `this.connection` caches the
     * connected client for the life of the service, so the tab kept calling into a session that no
     * longer existed and every tool failed.
     *
     * That cost a whole debugging session on 2026-09-02: the model reported that image search was
     * broken, and the search itself was fine end to end — the tab was talking to a server that had
     * been restarted underneath it, and the message named the wrong component.
     *
     * **Retrying is safe precisely because the session was not found.** A request that fails that way
     * never reached a live transport, so nothing ran and nothing can run twice. Only that error
     * qualifies; anything else is re-thrown untouched, because a tool that really failed must not be
     * silently repeated.
     */
    private async withSession<T>(run: (client: Client) => Promise<T>): Promise<T> {
        const client = await this.connect();

        try {
            return await run(client);
        } catch (error) {
            if (!isLostSession(error)) {
                throw error;
            }

            console.warn('AI chat (MCP): the server lost our session — reconnecting and retrying once');
            this.connection = null;
            // A new session is the one event that already signals "don't assume anything about
            // server state" — piggyback the tools cache invalidation on it rather than adding a
            // second, unrelated trigger.
            this.toolsCache = null;

            return run(await this.connect());
        }
    }

    private async connect(): Promise<Client> {
        if (!this.connection) {
            this.connection = this.createConnection().catch((error: unknown) => {
                // Don't cache a failed connection attempt — the next getTools() call (e.g. the
                // user retries after starting the server) should try again from scratch.
                this.connection = null;
                throw error;
            });
        }

        return this.connection;
    }

    private async createConnection(): Promise<Client> {
        const [{ Client: McpClient }, { StreamableHTTPClientTransport }] = await Promise.all([
            import('@modelcontextprotocol/sdk/client/index.js'),
            import('@modelcontextprotocol/sdk/client/streamableHttp.js'),
        ]);

        const client = new McpClient({ name: 'sendpulse-lp-constructor-chat', version: '0.0.1' });
        // `fetch`, not `requestInit.headers`: the latter is captured once at construction, and this
        // connection outlives both the open page (the user can navigate to another one) and the
        // access token. Injecting per request keeps every tool call scoped to whatever is open NOW.
        await client.connect(
            // The SDK transport takes a `URL`, which cannot be built from a relative path alone —
            // resolved against the current origin, which is the whole point of the relative path.
            new StreamableHTTPClientTransport(new URL(MCP_SERVER_URL, window.location.origin), {
                fetch: (url, init) => this.fetchWithPageContext(url, init),
            })
        );
        return client;
    }

    /**
     * Adds the page scope the MCP server requires on every request (see `http-app.ts`'s
     * `withRequestContext`): which page to mutate, and the caller's own token to authorize it with.
     *
     * Locked: the server refuses any call that names another page, and lists no `siteId`/`pageId`
     * arguments or site tools to this connection — results land in this tab's store, which shows
     * the open page only.
     */
    private async fetchWithPageContext(url: string | URL, init?: RequestInit): Promise<Response> {
        const [siteId, page] = await Promise.all([
            firstValueFrom(this.siteFacade.id$),
            firstValueFrom(this.pagesFacade.currentPage$),
        ]);

        // `Headers` rather than an object spread: `init.headers` arrives as a `Headers` instance
        // from the SDK's own transport, which a spread would silently flatten to nothing.
        const headers = new Headers(init?.headers);
        headers.set('Authorization', `Bearer ${this.cookieService.get(ACCESS_TOKEN_COOKIE)}`);
        headers.set('X-Site-Id', String(siteId ?? ''));
        headers.set('X-Page-Id', String(page?.id ?? ''));
        headers.set('X-Page-Scope', 'locked');

        return fetch(url, { ...init, headers });
    }
}
